import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  CircularProgress,
  LinearProgress,
  Alert
} from '@mui/material';
import { ArrowForward, CheckCircle, CloudUpload, Info } from '@mui/icons-material';
import BookUploader from '../BookUploader';
import { useBook } from '../../Context/BookContext';
import { authenticatedGet } from '../../utils/api';
import { Book } from '../../types';

const getUsersBooksEndpoint = process.env.REACT_APP_LAMBDA_GET_USERS_BOOKS;

interface UploadStageProps {
  onNext: () => void;
}

// Processing status order and their display info
const processingStages = [
  { status: 'uploaded', label: 'Uploaded', description: 'File uploaded successfully' },
  { status: 'processing_upload', label: 'Processing Upload', description: 'Storing file and metadata' },
  { status: 'waiting_for_info', label: 'Queued for Analysis', description: 'Waiting to extract book information' },
  { status: 'processing_info', label: 'Extracting Info', description: 'Parsing content and creating chapters' },
  { status: 'waiting_for_summary', label: 'Queued for Summary', description: 'Waiting to generate summaries' },
  { status: 'processing_summary', label: 'Creating Summary', description: 'Analyzing content with AI' },
  { status: 'waiting_for_subjects', label: 'Queued for Subjects', description: 'Waiting to extract subjects' },
  { status: 'processing_subjects', label: 'Finding Subjects', description: 'Identifying characters and objects' },
  { status: 'waiting_for_interesting', label: 'Queued for Final Step', description: 'Waiting for final processing' },
  { status: 'finding_interesting_subjects', label: 'Finding Best Subjects', description: 'Ranking subjects for visualization' },
  { status: 'completed', label: 'Completed', description: 'Book is ready for styling and images!' }
];

const UploadStage: React.FC<UploadStageProps> = ({ onNext }) => {
  const { selectedBook, refreshSelectedBook, lastUploadedBook, setSelectedBook } = useBook();
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string>('Calculating...');
  const pollingActiveRef = React.useRef<boolean>(false);
  const currentBookIdRef = React.useRef<string | null>(null);
  const lastProcessedUploadIdRef = React.useRef<string | null>(null);
  const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Get current processing stage info
  const getCurrentStageInfo = () => {
    if (!selectedBook) return null;
    
    const currentStage = processingStages.find(
      stage => stage.status === selectedBook.ProcessingStatus
    );
    
    return currentStage || null;
  };

  // Calculate progress percentage
  const getProgressPercentage = (): number => {
    if (!selectedBook) return 0;
    
    const currentIndex = processingStages.findIndex(
      stage => stage.status === selectedBook.ProcessingStatus
    );
    
    if (currentIndex === -1) return 0;
    
    return Math.round(((currentIndex + 1) / processingStages.length) * 100);
  };

  // Effect to stop polling when processing is complete
  useEffect(() => {
    const processingStatus = selectedBook?.ProcessingStatus;
    const isFinished = processingStatus === 'completed' || processingStatus?.startsWith('error_');
    
    if (pollingActiveRef.current && isFinished) {
      console.log(`Polling complete: status is ${processingStatus}`);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollingActiveRef.current = false;
    }
  }, [selectedBook?.ProcessingStatus]);

  // Effect to manage polling lifecycle based on book selection
  useEffect(() => {
    const bookId = selectedBook?.FileId;
    const processingStatus = selectedBook?.ProcessingStatus;
    const bookTitle = selectedBook?.Title;
    const bookFileName = selectedBook?.FileName;
    
    // Check if book ID actually changed
    const bookChanged = currentBookIdRef.current !== bookId;
    
    if (bookChanged) {
      // Book changed - stop old polling and reset for new book
      console.log(`Book changed from ${currentBookIdRef.current} to ${bookId}, resetting polling state`);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollingActiveRef.current = false;
      currentBookIdRef.current = bookId || null;
    }
    
    // Start polling if needed and not already active
    const isFinished = processingStatus === 'completed' || processingStatus?.startsWith('error_');
    const shouldStartPolling = bookId && 
                               !isFinished &&
                               !pollingActiveRef.current;
    
    if (shouldStartPolling) {
      console.log(`Starting polling for book: ${bookTitle || bookFileName || bookId}`);
      pollingActiveRef.current = true;
      
      pollIntervalRef.current = setInterval(async () => {
        // Only refresh if we still have the same book selected
        if (currentBookIdRef.current === bookId) {
          await refreshSelectedBook();
        }
      }, 5000);
    }

    // Cleanup only on actual unmount or book change
    return () => {
      if (pollIntervalRef.current) {
        console.log('UploadStage unmounting or book changed, stopping polling');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        pollingActiveRef.current = false;
      }
    };
    // Only depend on FileId to avoid rerunning on every status change
    // Status changes are handled by the separate effect above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook?.FileId]);

  // Auto-select uploaded book and start monitoring
  useEffect(() => {
    if (!lastUploadedBook) return;
    
    const uploadedBookId = (lastUploadedBook as any).FileId;
    
    // Only process if we haven't already
    if (lastProcessedUploadIdRef.current === uploadedBookId) {
      return;
    }
    
    console.log('New book uploaded in UploadStage:', uploadedBookId);
    lastProcessedUploadIdRef.current = uploadedBookId;
    
    // Fetch and select the uploaded book
    const fetchAndSelectBook = async () => {
      try {
        if (!getUsersBooksEndpoint) {
          console.error('getUsersBooksEndpoint not configured');
          return;
        }
        
        const url = `${getUsersBooksEndpoint}?FileId=${uploadedBookId}`;
        console.log('Fetching uploaded book:', url);
        
        const response = await authenticatedGet<{books: Book[]}>(url);
        
        if (response.data?.books && response.data.books.length > 0) {
          const uploadedBook = response.data.books[0];
          console.log('Setting uploaded book as selected:', uploadedBook.Title || uploadedBook.FileName);
          setSelectedBook(uploadedBook);
        } else {
          console.log('Book not in DB yet, will retry...');
          // Retry after a delay
          setTimeout(fetchAndSelectBook, 2000);
        }
      } catch (error) {
        console.error('Error fetching uploaded book:', error);
        // Retry after a delay
        setTimeout(fetchAndSelectBook, 2000);
      }
    };
    
    // Small delay to let backend process
    setTimeout(fetchAndSelectBook, 1000);
  }, [lastUploadedBook, setSelectedBook]);

  // Estimate time remaining based on actual processing times
  useEffect(() => {
    if (!selectedBook || selectedBook.ProcessingStatus === 'completed') {
      setEstimatedTimeRemaining('Complete!');
      return;
    }

    const currentStageIndex = processingStages.findIndex(
      stage => stage.status === selectedBook.ProcessingStatus
    );
    
    if (currentStageIndex === -1) {
      setEstimatedTimeRemaining('Calculating...');
      return;
    }

    // Time estimates for each stage (in seconds) - total ~5 minutes
    const stageTimings: { [key: string]: number } = {
      'uploaded': 5,
      'processing_upload': 5,
      'waiting_for_info': 5,
      'processing_info': 80,           // SLOW - GetBookInformation
      'waiting_for_summary': 5,
      'processing_summary': 25,
      'waiting_for_subjects': 5,
      'processing_subjects': 80,       // SLOW - GetSubjectsAndDescriptions
      'waiting_for_interesting': 5,
      'finding_interesting_subjects': 80, // SLOW - GetInterestingSubjects
    };

    // Calculate remaining time from current stage onwards
    let estimatedSeconds = 0;
    for (let i = currentStageIndex; i < processingStages.length - 1; i++) {
      const stageStatus = processingStages[i].status;
      estimatedSeconds += stageTimings[stageStatus] || 5;
    }
    
    if (estimatedSeconds < 60) {
      setEstimatedTimeRemaining(`~${estimatedSeconds}s`);
    } else {
      const minutes = Math.ceil(estimatedSeconds / 60);
      setEstimatedTimeRemaining(`~${minutes} min`);
    }
  }, [selectedBook]);

  const isProcessingComplete = selectedBook?.ProcessingStatus === 'completed';
  const hasError = selectedBook?.ProcessingStatus?.startsWith('error_');
  const currentStageInfo = getCurrentStageInfo();

  return (
    <Box>
      {/* Upload Card - Only show when no book is selected */}
      {!selectedBook && (
        <Card sx={{ 
          mb: 3, 
          border: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'background.paper'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Upload Your Book
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Upload an EPUB file to begin processing
                </Typography>
              </Box>
            </Box>
            
            <BookUploader />
          </CardContent>
        </Card>
      )}

      {/* Processing Status Card */}
      {selectedBook && (
        <Card sx={{ 
          border: '2px solid',
          borderColor: isProcessingComplete ? 'success.main' : hasError ? 'error.main' : 'warning.main',
          backgroundColor: 'background.paper'
        }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              {isProcessingComplete ? (
                <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <CircularProgress size={48} sx={{ color: 'warning.main' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                  {isProcessingComplete 
                    ? '✅ Processing Complete!' 
                    : hasError 
                      ? '❌ Processing Error'
                      : '⚙️ Processing Your Book'
                  }
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {selectedBook.Title || selectedBook.FileName}
                </Typography>
              </Box>
            </Box>

            {/* Progress Bar */}
            {!hasError && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Progress: {getProgressPercentage()}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {isProcessingComplete ? 'Complete!' : `Est. time remaining: ${estimatedTimeRemaining}`}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={getProgressPercentage()} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: isProcessingComplete ? 'success.main' : 'warning.main'
                    }
                  }}
                />
              </Box>
            )}

            {/* Current Status */}
            {currentStageInfo && !hasError && (
              <Box sx={{ 
                p: 3, 
                backgroundColor: isProcessingComplete ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                borderRadius: 2,
                border: '1px solid',
                borderColor: isProcessingComplete ? 'success.main' : 'warning.main',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                  {currentStageInfo.label}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {currentStageInfo.description}
                </Typography>
              </Box>
            )}

            {/* Error Display */}
            {hasError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Processing failed at stage: {currentStageInfo?.label || 'Unknown'}
                </Typography>
                <Typography variant="caption">
                  Please try uploading the book again or contact support if the issue persists.
                </Typography>
              </Alert>
            )}

            {/* Processing Complete Message */}
            {isProcessingComplete && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Your book has been successfully processed and is ready for the next steps!
                </Typography>
              </Alert>
            )}

            {/* Next Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={onNext}
                disabled={!isProcessingComplete}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.dark',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(116, 164, 137, 0.3)'
                  },
                  '&:disabled': {
                    backgroundColor: 'action.disabled'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  px: 4,
                  transition: 'all 0.3s ease'
                }}
              >
                Next: Styling
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* How This Works Info Box */}
      <Card sx={{ 
        mt: 4,
        border: '2px solid',
        borderColor: 'info.main',
        backgroundColor: 'info.light'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Info sx={{ color: 'info.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              How This Works
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            When you upload an EPUB file, our system parses your entire book to understand its structure and content. 
            We use AI to analyze the text, breaking it into chapters and generating detailed summaries of the story. 
            The system then identifies all the characters, objects, and settings described throughout your book. 
            Finally, we rank these subjects to find the most visually interesting and story-relevant elements that will make great illustrations. 
            Once this analysis is complete, we automatically generate a test image based on the book, this image will be visible in the next step where you can refine the visual style. 
            This process costs 1 credit since each generated image costs 1 credit.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UploadStage;

